import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserContext } from '@/auth/UserContext';
import { useSwitchProfile } from '@/hooks/useSwitchProfile';
import {
  PARENT_ROLES,
  type ProfileEntryResponse,
  type ProfileIdentity,
  type StudyYearResponse,
  type UserProfilesResponse,
} from '@/types/user';

interface UseProfileSelectionResult {
  profiles: UserProfilesResponse | undefined;
  studyYears: StudyYearResponse[];
  selectedStudyYear: StudyYearResponse | undefined;
  selectedYearIndex: number;
  activeProfiles: ProfileEntryResponse[];
  inactiveProfiles: ProfileEntryResponse[];
  inactiveNotificationCount: number;
  canAddStudent: boolean;
  pageTitle: string;
  selectYear: (index: number) => void;
  switchProfile: (identity: ProfileIdentity) => Promise<void>;
}

export const useProfileSelection = (): UseProfileSelectionResult => {
  const { t } = useTranslation();
  const { user, profiles } = useUserContext();
  const { switchProfile } = useSwitchProfile();
  const [selectedYear, setSelectedYear] = useState<number | null>(null);

  const studyYears = profiles?.studyYears ?? [];
  const currentYearIndex = studyYears.findIndex(
    (studyYear) => studyYear.isCurrent,
  );
  const defaultYearIndex = currentYearIndex === -1 ? 0 : currentYearIndex;
  const selectedYearIndex = selectedYear ?? defaultYearIndex;
  const selectedStudyYear = studyYears[selectedYearIndex];
  const inactiveProfiles = selectedStudyYear?.inactiveProfiles ?? [];

  return {
    profiles,
    studyYears,
    selectedStudyYear,
    selectedYearIndex,
    activeProfiles: selectedStudyYear?.profiles ?? [],
    inactiveProfiles,
    inactiveNotificationCount: inactiveProfiles.reduce(
      (sum, profile) => sum + profile.notifications.count,
      0,
    ),
    canAddStudent:
      user?.personType !== undefined && PARENT_ROLES.has(user.personType),
    pageTitle: user?.personType
      ? t(`profile.pageTitleByRole.${user.personType}`)
      : t('profile.pageTitle'),
    selectYear: setSelectedYear,
    switchProfile,
  };
};
